<template>
  <v-dialog :value="value" max-width="960" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title>{{ dialogTitle }}</v-card-title>
      <v-card-text>
        <v-alert v-if="sendError" type="error" dense class="mb-4">
          {{ sendError }}
        </v-alert>
        <v-progress-linear v-if="loading" indeterminate class="mb-4" />
        <p v-else-if="!items.length" class="lightText--text mb-0">
          {{ $t('spedilink.notifications.empty') }}
        </p>
        <v-simple-table v-else>
          <thead>
            <tr>
              <th>{{ $t('spedilink.notifications.route') }}</th>
              <th>{{ $t('spedilink.notifications.publishedAt') }}</th>
              <th>{{ $t('spedilink.notifications.linkName') }}</th>
              <th v-if="scope === NOTIFICATION_SCOPE.COMPANY">{{ $t('spedilink.notifications.author') }}</th>
              <th class="text-right" />
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.orderId">
              <td>{{ item.route }}</td>
              <td>{{ item.offerPublishedAt | dateFormat('DD.MM.YYYY HH:mm') }}</td>
              <td>{{ formatLinkName(item) }}</td>
              <td v-if="scope === NOTIFICATION_SCOPE.COMPANY">
                <div>{{ item.authorName }}</div>
                <div class="lightText--text caption">{{ item.companyName }}</div>
              </td>
              <td>
                <div class="d-flex align-center justify-end">
                  <v-btn
                    icon
                    class="mr-1"
                    :disabled="actionLoading"
                    @click="openOfferDetails(item)"
                  >
                    <v-icon size="25" color="primary">$infoBox</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    class="mr-1"
                    :disabled="actionLoading"
                    @click="sendOne(item)"
                  >
                    <v-icon color="primary">mdi-send-outline</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    :disabled="actionLoading"
                    @click="removeOne(item)"
                  >
                    <v-icon color="error">mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <OfferDetailsDialog
          v-if="selectedOfferId"
          :value="offerDetailsActive"
          :item-id="selectedOfferId"
          :is-vehicle="selectedIsVehicle"
          @closed="onOfferDetailsClosed"
        />
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-btn text @click="close">
          {{ $t('common.close') }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="error"
          outlined
          :disabled="!items.length || actionLoading"
          @click="clearAll"
        >
          {{ $t('spedilink.notifications.clearAll') }}
        </v-btn>
        <v-btn
          color="primary"
          depressed
          class="ml-2"
          :disabled="!items.length || actionLoading"
          @click="sendAll"
        >
          {{ $t('spedilink.notifications.sendAll') }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <Confirm ref="confirm" />
  </v-dialog>
</template>

<script>
import Service from '@/services'
import Confirm from '@/components/dialogs/Confirm.vue'

const NOTIFICATION_SCOPE = {
  PRIVATE: 0,
  COMPANY: 1
}

export default {
  name: 'SpedilinkPendingNotificationsDialog',
  components: {
    Confirm,
    OfferDetailsDialog: () => import('@/views/Market/dialogs/OfferDetailsDialog.vue')
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    scope: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      NOTIFICATION_SCOPE,
      items: [],
      loading: false,
      actionLoading: false,
      sendError: null,
      offerDetailsActive: false,
      selectedOfferId: null,
      selectedIsVehicle: false
    }
  },
  computed: {
    dialogTitle () {
      return this.scope === NOTIFICATION_SCOPE.COMPANY
        ? this.$t('spedilink.notifications.dialogTitleCompany')
        : this.$t('spedilink.notifications.dialogTitlePrivate')
    }
  },
  watch: {
    value (open) {
      if (open) {
        this.sendError = null
        this.loadItems()
      }
    }
  },
  methods: {
    formatLinkName (item) {
      const extra = item.additionalLinksCount > 0 ? ` +${item.additionalLinksCount}` : ''
      return `${item.primaryLinkName}${extra}`
    },
    async loadItems () {
      this.loading = true
      try {
        const { data } = await Service.get('spedilink/SpediLinkNotifications/GetList', { scope: this.scope })
        this.items = Array.isArray(data) ? data : []
      } catch (e) {
        this.items = []
      } finally {
        this.loading = false
      }
    },
    openOfferDetails (item) {
      this.selectedOfferId = item.orderId
      this.selectedIsVehicle = item.orderType === 'Vehicle'
      this.offerDetailsActive = true
    },
    onOfferDetailsClosed () {
      this.offerDetailsActive = false
      this.selectedOfferId = null
    },
    close () {
      this.$emit('input', false)
      this.$emit('closed')
    },
    async sendOne (item) {
      await this.sendNotifications(item.orderId)
    },
    async sendAll () {
      await this.sendNotifications(null)
    },
    async sendNotifications (orderId) {
      this.actionLoading = true
      this.sendError = null
      try {
        await Service.post('spedilink/SpediLinkNotifications/Send', {
          scope: this.scope,
          orderId
        })
        await this.$store.dispatch('setSuccessNotification', this.$t('spedilink.notifications.sendSuccess'))
        await this.loadItems()
        this.$emit('changed')
        if (!this.items.length) {
          this.close()
        }
      } catch (e) {
        this.sendError = e.response?.data?.Title || e.response?.data?.title || this.$t('spedilink.notifications.sendErrorUnknown')
      } finally {
        this.actionLoading = false
      }
    },
    async removeOne (item) {
      this.actionLoading = true
      this.sendError = null
      try {
        await Service.post('spedilink/SpediLinkNotifications/Remove', {
          scope: this.scope,
          orderId: item.orderId
        })
        await this.loadItems()
        this.$emit('changed')
      } catch (e) {
        this.sendError = e.response?.data?.Title || this.$t('spedilink.notifications.sendErrorUnknown')
      } finally {
        this.actionLoading = false
      }
    },
    async clearAll () {
      const confirmed = await this.$refs.confirm.open(
        this.$t('spedilink.notifications.clearAllTitle'),
        this.$t('spedilink.notifications.clearAllMessage')
      )
      if (!confirmed) return

      this.actionLoading = true
      this.sendError = null
      try {
        await Service.post('spedilink/SpediLinkNotifications/Remove', {
          scope: this.scope,
          orderId: null
        })
        await this.loadItems()
        this.$emit('changed')
      } catch (e) {
        this.sendError = e.response?.data?.Title || this.$t('spedilink.notifications.sendErrorUnknown')
      } finally {
        this.actionLoading = false
      }
    }
  }
}
</script>
